import { API, graphqlOperation } from "aws-amplify";
import React, { useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Loading from "_shared/components/Loading/Loading";
import "./CrossAssetClassVolatility.scss";
import CrossAssetChart from "./CrossAssetChart";
// import { isMobile } from "react-device-detect";

export default function CrossAssetClassVolatility(props) {
  const [period, setPeriod] = useState("3M");
  const [data, setData] = useState([]);
  const [rows, setRows] = useState([]);
  const [update, setUpdate] = useState(new Date().toISOString());

  const dict = {
    vix: "Stock Market Volatility",
    move: "Treasury Market Volatility",
    csi: "Corp Bond Market Volatility",
    jpmvxygl: "Currency Market Volatility",
    lf98oas: "High Yield Bond Market Volatility",
    vvix: "Volatility of Volatility – Market Uncertainty",
    vxtlt: "Bond Market Volatility",
  };

  const altDict = {
    vix: "Chicago Board Options Exchange Volatility Index",
    move: "ICE BofA MOVE Index",
    csi: "US Corp BBB/Baa - Treasury 10 Year Spread",
    jpmvxygl: "J.P. Morgan Global FX Volatility Index",
    lf98oas: "Bloomberg US Corporate High Yield Average OAS",
    vvix: "VVIX/VIX",
    vxtlt: "Cboe 20+ Year Treasury Bond ETF Volatility Index",
  };

  const infoTips = {
    vix: "(VIX INDEX) The CBOE Volatility Index is a real-time index that represents the market’s expectations for the relative strength of near-term price changes of the S&P 500 index (SPX). Because it is derived from the prices of SPX index options with near-term expiration dates, it generates a 30-day forward projection of volatility.",
    move: "(MOVE INDEX) The ICE BofA MOVE index tracks volatility in short-term Treasuries.  It is a yield curve weighted index of the normalized implied volatility on 1-month Treasury options. It is the weighted average of volatilities on the CT2, CT5, CT10, and CT30",
    csi: "US Corp BBB/Baa - Treasury 10 Year Spread",
    jpmvxygl:
      "(VXY INDEX) The J.P. Morgan Global FX Volatility Index is a liquidity-weighted average of 3-month implied volatility based on USD-based pairs' options market turnover.",
    lf98oas:
      "(LF98OAS INDEX) Daily value of the Bloomberg US Corporate High Yield Average OAS",
    vvix: "The ratio of VVIX Index to VIX Index, where VVIX Index is a volatility of volatility measure in that it represents the expected volatility of the 30-day forward price of the CBOE Volatility Index (VIX Index)",
    vxtlt:
      "(TYVIX INDEX) The CBOE/CBOT 10-Year US Treasury Note Volatility Index tracks the 10-Year's Volatility.",
  };

  React.useEffect(() => {
    getData();
  }, []);

  async function getData() {
    const response = (
      await API.graphql(
        graphqlOperation(`{
        get_cacv_data {
          data
        }
        cacv(order_by: {order: asc}) {
          id
          ticker
        }
      }`),
      )
    ).data;
    setData(response?.get_cacv_data?.data || []);
    setRows(response?.cacv || []);
  }

  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(rows);
    const [reorderedItem] = items?.splice(result.source.index, 1);
    items?.splice(result.destination.index, 0, reorderedItem);
    setRows(items);
    setUpdate(new Date().toISOString());
    await API.graphql(
      graphqlOperation(`mutation{
      ${items?.map(
        (obj, i) =>
          `update${i}: update_cacv_by_pk(pk_columns: {id: "${obj.id}"}, _set: {order: ${i}}) {id}`,
      )}
    }`),
    );
  };

  return (
    <div>
      <div className="dashboard-content asset-class-page">
        {data.length !== 0 && rows.length !== 0 ? (
          <div
            className="cross-asset-class-volatility"
            style={{ minHeight: 1800 }}
          >
            <div className="buttons">
              <div
                onClick={() => {
                  setPeriod("5D");
                }}
                className={`button 5D ${period === "5D" ? "selected" : ""}`}
              >
                1W
              </div>
              <div
                onClick={() => {
                  setPeriod("1M");
                }}
                className={`button 1M ${period === "1M" ? "selected" : ""}`}
              >
                1M
              </div>
              <div
                onClick={() => {
                  setPeriod("3M");
                }}
                className={`button 3M ${period === "3M" ? "selected" : ""}`}
              >
                3M
              </div>
              <div
                onClick={() => {
                  setPeriod("1Y");
                }}
                className={`button 1Y ${period === "1Y" ? "selected" : ""}`}
              >
                1Y
              </div>
            </div>
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="droppable" direction="vertical">
                {(provided, snapshot) => (
                  // <>{console.log(provided)}</>
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {(rows?.map)((chart, index) => (
                      <Draggable
                        key={`key-${index}`}
                        draggableId={`key-${index}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <CrossAssetChart
                              ticker={chart.ticker.split(" ")[0].toLowerCase()}
                              title={
                                dict[chart.ticker.split(" ")[0].toLowerCase()]
                              }
                              altTitle={
                                altDict[
                                  chart.ticker.split(" ")[0].toLowerCase()
                                ]
                              }
                              data={data?.map((obj) =>
                                chart.ticker.split(" ")[0].toLowerCase() ===
                                "vvix"
                                  ? (obj.vvix / obj.vix).toFixed(2)
                                  : obj[
                                      chart.ticker.split(" ")[0].toLowerCase()
                                    ],
                              )}
                              labels={data?.map((obj) => obj.date)}
                              period={period}
                              update={update}
                              info={
                                infoTips[
                                  chart.ticker.split(" ")[0].toLowerCase()
                                ]
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              height: "80vh",
              alignItems: "center",
            }}
          >
            <Loading variant="inner" />
          </div>
        )}
      </div>
      <div
        className="scroll-arrow"
        style={{
          position: "fixed",
          bottom: 10,
          width: "100%",
          left: "auto",
          right: "auto",
          color: "#bbb8b8",
          fontSize: "40px",
          opacity: "30%",
          cursor: "pointer",
        }}
      >
        {/* {isMobile ? (
          <div>
            <i
              className='fa fa-arrow-circle-up'
              onClick={() => {
                window.scrollTo({ top: window.scrollY - 90, behavior: "smooth" });
              }}></i>
            &nbsp;&nbsp;
            <i
              className='fa fa-arrow-circle-down'
              onClick={() => {
                window.scrollTo({ top: window.scrollY + 90, behavior: "smooth" });
              }}></i>
          </div>
        ) : (
          <div></div>
        )} */}
      </div>
    </div>
  );
}
